/**
 * Enhanced localStorage/sessionStorage service
 * Provides same-tab change detection via custom events
 * Wraps native Storage API with event dispatching
 */

/** Storage change event detail */
export interface StorageChangeDetail {
  key: string
  newValue: string | null
  oldValue: string | null
  timestamp: number
}

/** Storage change event type */
export type StorageEventType = 'same-tab' | 'cross-tab'

/** Callback for storage changes */
export type StorageChangeCallback = (
  newValue: string | null,
  oldValue: string | null,
  eventType: StorageEventType
) => void

/** Cleanup function to remove event listeners */
export type CleanupFunction = () => void

/**
 * Enhanced storage service with change detection
 * Wraps localStorage or sessionStorage with custom events
 */
export class StorageService {
  private eventName: string
  private _store: Storage

  /**
   * Create storage service instance
   * @param store - Storage to wrap (localStorage or sessionStorage)
   */
  constructor(store: Storage = window.localStorage) {
    this.eventName = store === window.sessionStorage ? 'sessionStorageChange' : 'localStorageChange'
    this._store = store
  }

  /**
   * Set item in storage and dispatch change event
   * @param key - Storage key
   * @param value - Value to store
   */
  setItem(key: string, value: string): void {
    const oldValue = this._store.getItem(key)

    // Set the item
    this._store.setItem(key, value)

    // Dispatch event for same-tab listeners
    this._dispatchEvent(key, value, oldValue)
  }

  /**
   * Remove item from storage and dispatch change event
   * @param key - Storage key to remove
   */
  removeItem(key: string): void {
    const oldValue = this._store.getItem(key)

    // Remove the item
    this._store.removeItem(key)

    // Dispatch event
    this._dispatchEvent(key, null, oldValue)
  }

  /**
   * Get item from storage
   * @param key - Storage key
   * @returns Stored value or null
   */
  getItem(key: string): string | null {
    return this._store.getItem(key)
  }

  /**
   * Dispatch custom storage change event
   * @private
   */
  private _dispatchEvent(key: string, newValue: string | null, oldValue: string | null): void {
    const event = new CustomEvent<StorageChangeDetail>(this.eventName, {
      detail: {
        key,
        newValue,
        oldValue,
        timestamp: Date.now(),
      },
    })
    window.dispatchEvent(event)
  }

  /**
   * Listen for storage changes on specific key
   * Handles both same-tab and cross-tab changes
   * @param key - Storage key to watch
   * @param callback - Function called when key changes
   * @returns Cleanup function to remove listeners
   * @example
   * const cleanup = storage.onChange('user_id', (newVal, oldVal, type) => {
   *   console.log(`User ID changed from ${oldVal} to ${newVal} (${type})`)
   * })
   * // Later: cleanup()
   */
  onChange(key: string, callback: StorageChangeCallback): CleanupFunction {
    // Handle same-tab changes
    const handleCustomEvent = (e: Event): void => {
      const customEvent = e as CustomEvent<StorageChangeDetail>
      if (customEvent.detail.key === key) {
        callback(customEvent.detail.newValue, customEvent.detail.oldValue, 'same-tab')
      }
    }

    // Handle cross-tab changes
    const handleStorageEvent = (e: StorageEvent): void => {
      if (e.key === key) {
        callback(e.newValue, e.oldValue, 'cross-tab')
      }
    }

    // Add listeners
    window.addEventListener(this.eventName, handleCustomEvent)
    window.addEventListener('storage', handleStorageEvent)

    // Return cleanup function
    return (): void => {
      window.removeEventListener(this.eventName, handleCustomEvent)
      window.removeEventListener('storage', handleStorageEvent)
    }
  }
} 

/** Pre-configured localStorage service instance */
export const storage = new StorageService(window.localStorage)

/** Pre-configured sessionStorage service instance */
export const storageSession = new StorageService(window.sessionStorage)